import React, { useState } from 'react';
import { Zap, TrendingUp, TrendingDown, Brain } from 'lucide-react';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';
import { Textarea } from '../design-system';

const MENTAL_STATES = ['Focus', 'Neutre', 'Tilté', 'Fatigué', 'Mixte'];

export default function SessionCloseModal({ games, onClose, onSave }) {
  const [mentalGlobal, setMentalGlobal] = useState('Focus');
  const [insights, setInsights] = useState('');
  const [saving, setSaving] = useState(false);

  const wins = games.filter(g => g.result === 'W').length;
  const losses = games.filter(g => g.result === 'L').length;
  const totalDeaths = games.reduce((a, g) => a + g.deaths, 0);
  const greedDeaths = games.reduce((a, g) => a + (g.deathDetails || []).filter(d => d.type === 'greed').length, 0);
  const avgDeaths = games.length > 0 ? (totalDeaths / games.length).toFixed(1) : 0;
  const positive = wins > losses;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ games_count: games.length, wins, losses, game_ids: games.map(g => g.id), mental_global: mentalGlobal, insights });
    } catch (e) {
      alert('Erreur : ' + e.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Récap · ${games.length} game${games.length > 1 ? 's' : ''}`}
      subtitle="Fermer la session"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>ANNULER</Button>
          <Button variant="primary" onClick={save} disabled={saving} loading={saving}>
            {saving ? 'SAUVEGARDE...' : 'FERMER LA SESSION'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={`border p-4 ${positive ? 'border-cyan-400/30 bg-cyan-400/5' : 'border-fuchsia-500/30 bg-fuchsia-500/5'}`}>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Résultat</div>
            <div className={`text-3xl font-black ${positive ? 'text-cyan-400' : 'text-fuchsia-400'}`}>
              {wins}<span className="text-zinc-600 text-xl">-{losses}</span>
            </div>
            {positive ? <TrendingUp size={12} className="text-cyan-400 mt-1" /> : <TrendingDown size={12} className="text-fuchsia-400 mt-1" />}
          </div>
          <div className="border border-zinc-800 p-4">
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Winrate</div>
            <div className="text-3xl font-black text-white">{games.length > 0 ? Math.round(wins / games.length * 100) : 0}<span className="text-zinc-600 text-xl">%</span></div>
          </div>
          <div className="border border-zinc-800 p-4">
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Morts/G</div>
            <div className={`text-3xl font-black ${avgDeaths <= 4 ? 'text-cyan-400' : 'text-rose-400'}`}>{avgDeaths}</div>
          </div>
          <div className="border border-zinc-800 p-4">
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Greed</div>
            <div className={`text-3xl font-black ${greedDeaths === 0 ? 'text-cyan-400' : 'text-amber-400'}`}>{greedDeaths}</div>
          </div>
        </div>

        {/* Games */}
        <div>
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-2">Games de la session</div>
          <div className="space-y-1.5">
            {games.map((g, i) => (
              <div key={g.id} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 p-2 text-xs">
                <span className="text-zinc-500 font-mono w-5">{i + 1}</span>
                <span className={`font-black ${g.result === 'W' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>{g.result}</span>
                <span className="text-white font-semibold w-24 truncate">{g.champion}</span>
                <span className="text-zinc-400 font-mono">{g.kills}/{g.deaths}/{g.assists}</span>
                <span className="text-zinc-500 ml-auto font-mono">{g.duration}min</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mental */}
        <div>
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold"><Brain size={12} className="inline mr-1" /> Mental Global</div>
          <div className="grid grid-cols-5 gap-2">
            {MENTAL_STATES.map(m => (
              <button key={m} onClick={() => setMentalGlobal(m)}
                className={`p-2 border text-xs font-semibold tracking-wider transition ${mentalGlobal === m ? 'bg-cyan-400 text-black border-cyan-400' : 'bg-transparent border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}>
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Insights */}
        <Textarea
          label={<><Zap size={12} className="inline mr-1" /> Insights / Takeaways</>}
          value={insights}
          onChange={(e) => setInsights(e.target.value)}
          placeholder="Ex: Bon contrôle de lane en early, tendance à surextend après kill..."
          rows={4}
        />
      </div>
    </Modal>
  );
}
